import React, { useState, useEffect, useRef } from "react";
import { api } from "../../lib/apiClient";
import { Download, Edit3, Plus, Save, Trash2, Upload, X } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { useConfirm } from "../../components/ConfirmDialog";

export function CategoriesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newName, setNewName] = useState("");
  const [importInfo, setImportInfo] = useState("");
  const fileRef = useRef();
  const confirm = useConfirm();
  const load = () => api.get("/quote-categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post("/quote-categories", { name });
      setNewName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create");
    }
  };
  const save = async () => {
    try {
      await api.put(`/quote-categories/${editing.id}`, { name: editing.name });
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };
  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this category?",
      description: "Parts assigned to it will keep working — they just lose this category tag.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/quote-categories/${id}`);
    load();
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/quote-categories/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const skip = data.skipped_duplicates ? `, ${data.skipped_duplicates} duplicate${data.skipped_duplicates === 1 ? "" : "s"} skipped` : "";
      setImportInfo(`Imported · ${data.inserted} new${skip}${data.errors?.length ? `, ${data.errors.length} errors` : ""}`);
    } catch (err) {
      setImportInfo(err?.response?.data?.detail || "Import failed");
    }
    e.target.value = "";
    load();
  };

  const downloadTemplate = () => {
    const csv = "name,depot,job_types\nIndustrial Door,Plymouth,Install|Repair\nAutomatic Door,Bristol,Install\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "categories-template.csv";
    a.click();
  };

  const downloadList = async () => {
    try {
      const res = await api.get("/quote-categories/export", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = "categories.csv";
      a.click();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not export");
    }
  };

  return (
    <div className="space-y-4" data-testid="categories-tab">
      <div className="text-xs text-slate-500 max-w-2xl">
        Categories appear in the Quote Builder's Customer &amp; Job section. Estimators must pick one before
        repair parts are shown. Renaming a category cascades into existing quotes and repair parts.
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="New category name (e.g. Industrial Door)"
          className="qb-input h-9 flex-1 max-w-md"
          data-testid="new-category-input"
        />
        <button
          onClick={add}
          className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark"
          data-testid="add-category-button"
        >
          <Plus size={14} /> Add Category
        </button>
        <button onClick={() => fileRef.current?.click()} className="h-9 px-4 border border-slate-900 hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="categories-import-button">
          <Upload size={14} /> Import CSV
        </button>
        <button onClick={downloadList} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="categories-export-button">
          <Download size={14} /> Export CSV
        </button>
        <button onClick={downloadTemplate} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="categories-template-button">
          <Download size={14} /> Template
        </button>
        <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden" />
        {importInfo && <span className="text-xs text-slate-500 ml-2 basis-full">{importInfo}</span>}
      </div>
      <div className="border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-900">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Name</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={2} className="px-3 py-6 text-center text-slate-400 text-sm">No categories yet.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="px-3 py-2">
                  {editing?.id === c.id ? (
                    <input
                      value={editing.name}
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && save()}
                      className="qb-input h-9 max-w-md"
                      autoFocus
                    />
                  ) : (
                    <span className="font-medium">{c.name}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right space-x-2">
                  {editing?.id === c.id ? (
                    <>
                      <button onClick={save} className="text-emerald-600 hover:text-emerald-800"><Save size={14} /></button>
                      <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditing({ id: c.id, name: c.name })} className="text-slate-500 hover:text-slate-900"><Edit3 size={14} /></button>
                      <button onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
